import type { SanityHardwareCardDocument, SanityPageBuilderHighlightedProductsSection, SanityPageBuilderSection } from "@ouster/shared";
import { HighlightedProductsBlockView } from "~/features/blocks/inventory/highlighted-products";
import { ProductCardView } from "~/features/card/product-card";
import { ProductFeaturedCardView } from "~/features/card/product-featured-card";
import { type Props as AssetProps } from "~/features/craft/asset";
import { Button } from "~/features/ui/button";
import { Link } from "~/features/ui/link";
import { isLinkValid } from "~/features/ui/link/utils";
import { toCraftAsset } from "~/features/sanity/media";
import { toCraftLink } from "~/features/sanity/link";
import type { Mode, Theme } from "~/features/ui/get-theme-style";
import ArrowRight from "~/public/icons/arrow-right.svg";
import ArrowTopRight from "~/public/icons/arrow-top-right.svg";
type Props = {
  section: SanityPageBuilderSection & {
    loaded?: boolean;
    sectionContent: SanityPageBuilderHighlightedProductsSection;
  };
};
type PlainLink = {
  href?: string;
  newWindow?: boolean;
  text?: string;
};
function getPlainLinkIcon(link?: PlainLink | null) {
  if (!link) {
    return undefined;
  }
  return link.newWindow ? ArrowTopRight : ArrowRight;
}
type HighlightedProductsViewProduct = {
  _id?: SanityHardwareCardDocument["_id"];
  asset?: AssetProps["asset"];
  id?: string | null;
  productCaption?: string | null;
  productHighlights?: string[] | null;
  title?: string | null;
  url?: string | null;
};
export function SanityHighlightedProductsSection({
  section
}: Props) {
  const {
    sectionContent,
    sectionSettings
  } = section;
  const mode = (sectionSettings?.mode || "light") as Mode;
  const theme = (sectionSettings?.theme || "white") as Theme;
  const products = sectionContent.products?.map<HighlightedProductsViewProduct>(product => ({
    _id: product._id,
    asset: toCraftAsset(product.productImage) as AssetProps["asset"],
    id: product._id,
    productCaption: product.productCaption,
    productHighlights: product.productHighlights,
    title: product.title,
    url: product.uri
  })) ?? [];
  const generations = sectionContent.products?.map(product => product.productGeneration?.title ?? null) ?? [];
  const cta = toCraftLink(sectionContent.link);
  const icon = getPlainLinkIcon(cta);
  return <HighlightedProductsBlockView anchor={{
    id: sectionSettings?.sectionId?.current || sectionSettings?.anchorName || section._key,
    name: sectionSettings?.anchorName
  }} generations={generations} getProductKey={(product, index) => product._id || index} hasFilter={sectionContent.hasFilter} hasTopDivider={sectionSettings?.hasTopDivider ?? true} headline={sectionContent.headline} isHighlighted={sectionContent.isHighlighted} link={isLinkValid(cta) ? <Button as={Link} {...cta} icon={icon} variant="underline" /> : undefined} mode={mode} products={products} renderProduct={(product, _index, {
    count,
    isDesktop,
    mode: productMode
  }) => count === 1 && isDesktop ? <ProductFeaturedCardView mode={productMode} {...product} /> : <ProductCardView mode={productMode} {...product} />} sectionProps={{
    "data-sanity-section-key": section._key,
    "data-sanity-section-type": section._type
  }} text={sectionContent.text} theme={theme} />;
}